import React, { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import WrappedProgressBar from './WrappedProgressBar'
import WrappedHeaderActions from './WrappedHeaderActions'
import LuxuryEscapesLogoWhite from 'components/App/Header/Logo/logos/LuxuryEscapesLogoWhite'
import { useWrappedStorySelector } from 'contexts/Wrapped'

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex.stickyFooter};
  padding: ${rem(16)} ${rem(8)} ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(15)};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
    backdrop-filter: blur(0);
    -webkit-backdrop-filter: blur(0);
    mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
    -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease, backdrop-filter 0.3s ease;
    pointer-events: none;
  }

  &.is-scrolled::before {
    opacity: 1;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
`

const ProgressContainer = styled.div`
  display: flex;
  gap: ${rem(4)};
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

interface Props {
  onClose: () => void
  onGoToSlide: (index: number) => void
}

function WrappedHeader({ onClose, onGoToSlide }: Props) {
  // Use granular selectors to minimize re-renders
  const slides = useWrappedStorySelector(state => state.slides)
  const currentIndex = useWrappedStorySelector(state => state.currentIndex)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isVideoReady = useWrappedStorySelector(state => state.isVideoReady)
  const isScrolled = useWrappedStorySelector(state => state.isScrolled)

  // Handle progress bar click using data attribute pattern to avoid anonymous functions
  const handleProgressBarClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const index = parseInt(e.currentTarget.dataset.index ?? '0', 10)
    onGoToSlide(index)
  }, [onGoToSlide])

  return (
    <Container className={isScrolled ? 'is-scrolled' : undefined}>
      <ProgressContainer>
        {slides.map((slide, index) => (
          <WrappedProgressBar
            key={slide.id}
            data-index={index}
            isActive={index === currentIndex}
            isCompleted={index < currentIndex}
            isPaused={isPaused || !isVideoReady}
            duration={slide.duration}
            onClick={handleProgressBarClick}
          />
        ))}
      </ProgressContainer>
      <HeaderRow>
        <LuxuryEscapesLogoWhite />
        <WrappedHeaderActions
          onClose={onClose}
        />
      </HeaderRow>
    </Container>
  )
}

export default React.memo(WrappedHeader)
